(function () {
  'use strict';

  const routes = window.routes || {};
  const body = document.body;
  const attributionStorageKey = 'ahanda:attribution:v13';
  const attributionTtl = 90 * 24 * 60 * 60 * 1000;
  let predictiveController;
  let predictiveTimer;

  // Key by script node so drawer replacements cannot reuse a stale product.
  const productDataCache = new WeakMap();
  function readProductData(json) {
    if (!json) return null;
    const text = json.textContent;
    const cached = productDataCache.get(json);
    if (cached && cached.text === text) return cached.product;
    try {
      const product = JSON.parse(text);
      productDataCache.set(json, { text, product });
      return product;
    } catch (error) { return null; }
  }

  function processingAllowed(purpose) {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return purpose === 'marketing'
        ? privacy?.marketingAllowed?.() === true
        : privacy?.analyticsProcessingAllowed?.() === true;
    } catch (error) { return false; }
  }

  function readStoredAttribution() {
    if (!processingAllowed('marketing')) return null;
    try {
      const stored = JSON.parse(window.localStorage.getItem(attributionStorageKey) || 'null');
      if (!stored || !stored.updatedAt || Date.now() - stored.updatedAt > attributionTtl) return null;
      return stored;
    } catch (error) {
      return null;
    }
  }

  function inferAiSource(referrer) {
    if (!referrer) return '';
    try {
      const hostname = new URL(referrer).hostname.toLowerCase();
      if (hostname === 'chatgpt.com' || hostname.endsWith('.chatgpt.com') || hostname === 'chat.openai.com') return 'chatgpt';
      if (hostname === 'perplexity.ai' || hostname.endsWith('.perplexity.ai') || hostname === 'gemini.google.com' || hostname === 'copilot.microsoft.com') return 'other_ai';
    } catch (error) {
      return '';
    }
    return '';
  }

  function sanitiseReferrer(referrer) {
    if (!referrer) return '';
    try {
      const url = new URL(referrer);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return '';
    }
  }

  function captureAttribution() {
    if (!processingAllowed('marketing')) return { firstTouch: null, lastTouch: null };
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer || '';
    const safeReferrer = sanitiseReferrer(referrer);
    const inferredSource = inferAiSource(referrer);
    let isExternalReferrer = false;
    try { isExternalReferrer = Boolean(referrer) && new URL(referrer).origin !== window.location.origin; } catch (error) { /* Ignore malformed referrers. */ }
    const source = params.get('utm_source') || inferredSource;
    const isAiSource = ['chatgpt', 'google_ai', 'other_ai'].includes(source);
    const medium = params.get('utm_medium') || (isAiSource ? 'organic_ai' : '');
    const campaign = params.get('utm_campaign') || (isAiSource ? 'geo_aeo_v13' : '');
    const content = params.get('utm_content') || '';
    const hasTouch = Boolean(source || medium || campaign || content || isExternalReferrer);
    let stored = readStoredAttribution() || { firstTouch: null, lastTouch: null, updatedAt: Date.now() };
    if (hasTouch) {
      const touch = {
        source: source || 'referral',
        medium: medium || 'referral',
        campaign,
        content,
        landingPath: window.location.pathname,
        referrer: safeReferrer
      };
      if (!stored.firstTouch) stored.firstTouch = touch;
      stored.lastTouch = touch;
      stored.updatedAt = Date.now();
      try { window.localStorage.setItem(attributionStorageKey, JSON.stringify(stored)); } catch (error) { /* Attribution is optional. */ }
    }
    return stored;
  }

  function getAttribution() {
    return captureAttribution() || { firstTouch: null, lastTouch: null };
  }

  function setAttributionFields(form) {
    const attribution = getAttribution();
    const touch = attribution.lastTouch || attribution.firstTouch || {};
    const values = {
      lead_source: touch.source || '',
      utm_source: touch.source || '',
      utm_medium: touch.medium || '',
      utm_campaign: touch.campaign || '',
      utm_content: touch.content || '',
      landing_path: touch.landingPath || window.location.pathname,
      referrer: processingAllowed('marketing') ? (touch.referrer || sanitiseReferrer(document.referrer || '')) : ''
    };
    Object.entries(values).forEach(([name, value]) => {
      const field = form.querySelector(`[data-attribution-field="${name}"]`);
      if (field) field.value = value;
    });
    return values;
  }

  function bindLeadAttribution() {
    captureAttribution();
    document.querySelectorAll('[data-attribution-form]').forEach((form) => {
      setAttributionFields(form);
      if (form.dataset.attributionBound) return;
      form.dataset.attributionBound = 'true';
      let started = false;
      form.addEventListener('focusin', () => {
        if (started) return;
        started = true;
        publishEvent('lead_form_start', { form_name: form.dataset.attributionForm || form.id || 'contact' });
      });
      form.addEventListener('submit', () => {
        const values = setAttributionFields(form);
        publishEvent('lead_form_submit', {
          form_name: form.dataset.attributionForm || form.id || 'contact',
          lead_source: values.lead_source,
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          utm_content: values.utm_content
        });
      });
    });
  }

  function publishEvent(name, payload = {}) {
    if (!processingAllowed('analytics')) return;
    const attribution = getAttribution();
    const touch = attribution.lastTouch || attribution.firstTouch || {};
    const detail = { ...payload, path: window.location.pathname, attribution: touch };
    document.dispatchEvent(new CustomEvent(`ahanda:${name}`, { detail }));
    if (['select_item', 'view_item', 'add_to_cart', 'funnel_click', 'checkout_intent', 'lead_form_start', 'lead_form_submit'].includes(name)) {
      window.dataLayer = window.dataLayer || [];
      if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: name,
        ...detail,
        ahanda_source: touch.source || '',
        ahanda_medium: touch.medium || '',
        ahanda_campaign: touch.campaign || '',
        ahanda_content: touch.content || ''
      });
    }
    try {
      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(`ahanda_${name}`, detail);
    } catch (error) {
      // Analytics must never interrupt shopping interactions.
    }
  }

  function ecommerceItem(product, variant, quantity = 1) {
    return {
      item_id: String(product.id), item_name: product.title,
      item_variant: variant.title, variant_id: String(variant.id),
      price: Number(variant.price) / 100, quantity: Number(quantity)
    };
  }

  function bindDtcAnalytics() {
    let viewed = false;
    const update = () => {
      if (!processingAllowed('marketing') && window.Shopify?.customerPrivacy) {
        try { window.localStorage.removeItem(attributionStorageKey); } catch (error) { /* Optional storage. */ }
      }
      document.querySelectorAll('[data-attribution-form]').forEach(setAttributionFields);
      if (viewed || !processingAllowed('analytics')) return;
      const form = document.querySelector('[data-dtc-product-form]');
      try {
        const product = readProductData(form?.querySelector('[data-product-json]'));
        const variant = product?.variants.find(v => String(v.id) === form.querySelector('[data-variant-id]')?.value);
        if (!variant) return;
        viewed = true;
        publishEvent('view_item', { ecommerce: { currency: window.theme?.currency || 'TRY', value: variant.price / 100, items: [ecommerceItem(product, variant)] } });
      } catch (error) { /* Measurement must not interrupt shopping. */ }
    };
    document.addEventListener('visitorConsentCollected', update);
    update();
    if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) update(); });
    }
    document.addEventListener('submit', event => {
      if (event.submitter?.name === 'checkout' && event.target.matches('form')) {
        publishEvent('checkout_intent', { source: event.target.closest('[data-cart-drawer]') ? 'cart_drawer' : 'cart_page' });
      }
    });
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} TL`;
    }
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = Number(count) < 1;
    });
  }

  async function getCart() {
    const response = await fetch(routes.cart_js_url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded');
    return response.json();
  }

  function getDrawer() {
    return document.querySelector('#CartDrawer');
  }

  function getSearchDrawer() {
    return document.querySelector('#SearchDrawer');
  }

  function getFilterPanel() {
    return document.querySelector('[data-filter-panel]');
  }

  function bindMegaMenus() {
    const menus = Array.from(document.querySelectorAll('[data-mega-menu]'));
    if (!menus.length) return;
    const desktop = window.matchMedia('(min-width: 901px)');
    const closeOthers = (current) => {
      menus.forEach((menu) => {
        if (menu !== current) menu.open = false;
      });
    };
    menus.forEach((menu) => {
      const summary = menu.querySelector(':scope > summary');
      if (!summary) return;
      menu.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        closeOthers(menu);
        menu.open = true;
      });
      menu.addEventListener('mouseleave', (event) => {
        if (!desktop.matches || (event.relatedTarget && menu.contains(event.relatedTarget))) return;
        if (!menu.matches(':focus-within')) menu.open = false;
      });
      summary.addEventListener('click', () => {
        if (desktop.matches) closeOthers(menu);
      });
    });
  }

  function bindLanguageSwitcher() {
    const switchers = Array.from(document.querySelectorAll('[data-language-switcher]'));
    if (!switchers.length) return;
    switchers.forEach((switcher) => {
      const trigger = switcher.querySelector('[data-language-switcher-trigger]');
      if (!trigger) return;
      const setOpen = (open) => {
        switcher.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', String(open));
      };
      switcher.addEventListener('mouseenter', () => setOpen(true));
      switcher.addEventListener('mouseleave', (event) => {
        if (event.relatedTarget && switcher.contains(event.relatedTarget)) return;
        if (!switcher.matches(':focus-within')) setOpen(false);
      });
      switcher.addEventListener('focusin', () => setOpen(true));
      switcher.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!switcher.matches(':focus-within')) setOpen(false);
        }, 0);
      });
      trigger.addEventListener('click', () => setOpen(!switcher.classList.contains('is-open')));
      const languageForm = switcher.querySelector('form');
      if (languageForm) languageForm.addEventListener('submit', () => {
        const destination = new URL(window.location.href);
        const params = new URLSearchParams();
        ['preview_theme_id', 'pb', 'variant'].forEach(key => {
          const value = destination.searchParams.get(key);
          if (value && /^\d+$/.test(value)) params.set(key, value);
        });
        const productForm = document.querySelector('[data-dtc-product-form]');
        if (productForm) {
          const id = productForm.querySelector('[data-variant-id]');
          if (id && !id.disabled && /^\d+$/.test(id.value)) params.set('variant', id.value);
          const grind = syncGrindState(productForm);
          params.set('ahanda_form', grind.isGround ? 'ground' : 'bean');
          const method = productForm.querySelector('[data-grind-method]');
          if (method && !method.disabled && method.value) params.set('ahanda_method', method.value);
        }
        const returnTo = languageForm.querySelector('[name="return_to"]');
        if (returnTo) returnTo.value = destination.pathname + (params.size ? '?' + params.toString() : '');
      });
    });
  }

  function openSearch() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    closeDrawer();
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    body.classList.add('is-locked');
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    const input = drawer.querySelector('[data-predictive-input]');
    if (input) window.setTimeout(() => input.focus(), 40);
  }

  function closeSearch() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    window.setTimeout(() => { drawer.hidden = true; }, 260);
    if (!getDrawer()?.classList.contains('is-open') && !getFilterPanel()?.classList.contains('is-open')) body.classList.remove('is-locked');
  }

  function openFilters() {
    const panel = getFilterPanel();
    const overlay = document.querySelector('[data-filter-overlay]');
    const trigger = document.querySelector('[data-filter-trigger]');
    if (!panel || !overlay) return;
    panel.classList.add('is-open');
    overlay.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    body.classList.add('is-locked');
    const closeButton = panel.querySelector('[data-filter-close]');
    if (closeButton) closeButton.focus();
  }

  function closeFilters() {
    const panel = getFilterPanel();
    const overlay = document.querySelector('[data-filter-overlay]');
    const trigger = document.querySelector('[data-filter-trigger]');
    if (!panel) return;
    panel.classList.remove('is-open');
    if (overlay) overlay.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (!getDrawer()?.classList.contains('is-open') && !getSearchDrawer()?.classList.contains('is-open')) body.classList.remove('is-locked');
  }

  function clearPredictiveResults() {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    const results = drawer.querySelector('[data-predictive-results]');
    const empty = drawer.querySelector('[data-predictive-empty]');
    const all = drawer.querySelector('[data-search-all]');
    const status = drawer.querySelector('[data-predictive-status]');
    if (results) results.replaceChildren();
    if (empty) empty.hidden = true;
    if (all) all.hidden = true;
    if (status) status.textContent = '';
  }

  function appendPredictiveGroup(container, label, items, type) {
    if (!items || !items.length) return 0;
    const group = document.createElement('section');
    group.className = 'predictive-group';
    const heading = document.createElement('h3');
    heading.textContent = label;
    group.appendChild(heading);
    const list = document.createElement('div');
    list.className = 'predictive-group__list';
    items.slice(0, 6).forEach((item) => {
      const link = document.createElement('a');
      link.className = 'predictive-result';
      link.href = item.url || '#';
      const imageUrl = type === 'product' ? (item.featured_image?.url || item.image) : type === 'article' ? item.image : item.featured_image?.url;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.title || '';
        image.loading = 'lazy';
        link.appendChild(image);
      }
      const content = document.createElement('span');
      content.className = 'predictive-result__content';
      const title = document.createElement('strong');
      title.textContent = item.title || item.text || '';
      content.appendChild(title);
      if (type === 'product' && item.price != null) {
        const price = document.createElement('small');
        price.textContent = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(item.price));
        content.appendChild(price);
      }
      link.appendChild(content);
      list.appendChild(link);
    });
    group.appendChild(list);
    container.appendChild(group);
    return Math.min(items.length, 6);
  }

  function renderPredictiveResults(data, query) {
    const drawer = getSearchDrawer();
    if (!drawer) return;
    const container = drawer.querySelector('[data-predictive-results]');
    const empty = drawer.querySelector('[data-predictive-empty]');
    const all = drawer.querySelector('[data-search-all]');
    const status = drawer.querySelector('[data-predictive-status]');
    if (!container) return;
    container.replaceChildren();
    const resultGroups = data?.resources?.results || {};
    const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
    const count = appendPredictiveGroup(container, isEnglish ? 'Coffee' : 'Kahveler', resultGroups.products, 'product')
      + appendPredictiveGroup(container, isEnglish ? 'Collections' : 'Koleksiyonlar', resultGroups.collections, 'collection')
      + appendPredictiveGroup(container, isEnglish ? 'Guides' : 'Rehberler', resultGroups.articles, 'article')
      + appendPredictiveGroup(container, isEnglish ? 'Pages' : 'Sayfalar', resultGroups.pages, 'page');
    if (empty) empty.hidden = count > 0;
    if (all) {
      all.href = `${routes.search_url || '/search'}?q=${encodeURIComponent(query)}`;
      all.hidden = count === 0;
    }
    if (status) status.textContent = count ? `${count} ${isEnglish ? 'suggestions' : 'öneri'}` : '';
  }

  async function fetchPredictiveResults(query) {
    if (!routes.predictive_search_url) return;
    if (predictiveController) predictiveController.abort();
    predictiveController = new AbortController();
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    const drawer = getSearchDrawer();
    const status = drawer && drawer.querySelector('[data-predictive-status]');
    const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
    if (status) status.textContent = isEnglish ? 'Searching…' : 'Aranıyor…';
    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, signal: predictiveController.signal });
      if (!response.ok) throw new Error('Predictive search failed');
      renderPredictiveResults(await response.json(), query);
    } catch (error) {
      if (error.name !== 'AbortError' && status) status.textContent = isEnglish ? 'Search is unavailable right now.' : 'Arama şu anda kullanılamıyor.';
    }
  }

  function schedulePredictiveSearch(input) {
    const query = input.value.trim();
    clearTimeout(predictiveTimer);
    if (query.length < 2) {
      clearPredictiveResults();
      return;
    }
    predictiveTimer = window.setTimeout(() => fetchPredictiveResults(query), 180);
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    body.classList.add('is-locked');
    const closeButton = drawer.querySelector('[data-cart-close]');
    if (closeButton) closeButton.focus();
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    body.classList.remove('is-locked');
    window.setTimeout(() => { drawer.hidden = true; }, 260);
  }

  async function refreshDrawer(shouldOpen) {
    const drawer = getDrawer();
    const wasOpen = drawer && drawer.classList.contains('is-open');
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
      if (response.ok) {
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('#CartDrawer');
        const current = getDrawer();
        if (next && current) current.replaceWith(next);
      }
      const cart = await getCart();
      setCartCount(cart.item_count);
      if (shouldOpen || wasOpen) openDrawer();
    } catch (error) {
      const status = document.querySelector('[data-global-status]');
      if (status) status.textContent = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'There was a problem updating your cart. Please try again.' : 'Sepeti güncellerken bir sorun oluştu. Lütfen tekrar dene.';
    }
  }

  async function changeLine(line, quantity) {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart line could not be updated');
    await refreshDrawer(true);
  }

  async function changeCartPageLine(line, quantity) {
    const response = await fetch(routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart page line could not be updated');
    window.location.reload();
  }

  function showFormError(form, message) {
    const element = form.querySelector('[data-form-status]');
    if (element) {
      element.hidden = false;
      element.textContent = message;
    }
  }

  function syncGrindState(form) {
    const formOption = form.querySelector('[data-form-option-input]:checked');
    const picker = form.querySelector('[data-form-picker]');
    const value = formOption ? formOption.value : picker ? picker.value : 'Çekirdek';
    const property = form.querySelector('[data-form-property]');
    const wrapper = form.querySelector('[data-grind-method-wrapper]');
    const method = form.querySelector('[data-grind-method]');
    const isGround = /öğütülmüş|ground/i.test(value || '');
    if (property) property.value = value;
    if (wrapper) wrapper.hidden = !isGround;
    if (method) {
      method.required = isGround;
      method.disabled = !isGround;
      if (method.dataset.fixedGrindMethod) method.value = method.dataset.fixedGrindMethod;
      else if (!isGround) method.value = '';
    }
    return { value, isGround };
  }

  function updateVariant(form) {
    const json = form.querySelector('[data-product-json]');
    const idInput = form.querySelector('[data-variant-id]');
    if (!json || !idInput) return;
    const product = readProductData(json);
    if (!product) return;
    const selected = Array.from(form.querySelectorAll('[data-option-position]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
    const variant = selected.length
      ? product.variants.find((candidate) => candidate.options.every((value, index) => value === selected[index]))
      : product.variants.find((candidate) => String(candidate.id) === idInput.value);
    idInput.value = variant ? variant.id : '';
    idInput.disabled = !variant;
    form.dataset.variantAvailable = variant?.available ? 'true' : 'false';
    const price = form.querySelector('[data-product-price]') || form.closest('[data-product-layout]')?.querySelector('[data-product-price]');
    const compare = form.querySelector('[data-compare-price]') || form.closest('[data-product-layout]')?.querySelector('[data-compare-price]');
    if (price) price.textContent = variant ? money(variant.price) : '—';
    if (compare) {
      compare.textContent = variant && variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
      compare.hidden = !(variant && variant.compare_at_price > variant.price);
    }
    const submit = form.querySelector('[data-add-to-cart]');
    if (submit) {
      const submitLabel = submit.querySelector('[data-add-to-cart-label-text]');
      const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
      const availableLabel = submit.dataset.addToCartLabel || (isEnglish ? 'Add to cart' : 'Sepete ekle');
      submit.disabled = !variant?.available || form.dataset.submitting === 'true';
      if (submitLabel) submitLabel.textContent = variant?.available ? availableLabel : (!variant ? (isEnglish ? 'Unavailable combination' : 'Bu seçenek yok') : (isEnglish ? 'Sold out' : 'Tükendi'));
      else submit.textContent = variant?.available ? availableLabel : (!variant ? (isEnglish ? 'Unavailable combination' : 'Bu seçenek yok') : (isEnglish ? 'Sold out' : 'Tükendi'));
    }
    syncGrindState(form);
    syncDtcPurchase(form);
  }

  function syncDtcPurchase(form) {
    if (!form.hasAttribute('data-dtc-product-form')) return;
    const page = form.closest('[data-product-page]');
    const bar = page?.querySelector('[data-dtc-purchase]');
    if (!bar) return;
    const submit = form.querySelector('[data-add-to-cart]');
    bar.querySelector('[data-dtc-price]').textContent = page.querySelector('[data-product-price]')?.textContent || '—';
    const selections = Array.from(form.querySelectorAll('[data-option-position]:checked')).map(input => input.value);
    const grind = form.querySelector('[data-form-property]')?.value;
    if (!form.querySelector('[data-form-option-input]') && grind) selections.push(grind);
    const method = form.querySelector('[data-grind-method]');
    if (method && !method.disabled && method.value) selections.push(method.value);
    bar.querySelector('[data-dtc-selection]').textContent = selections.join(' · ');
    const button = bar.querySelector('[data-dtc-submit]');
    button.disabled = submit.disabled;
    button.textContent = submit.textContent;
    bar.hidden = false;
  }

  function restoreProductSelection(form) {
    if (!form.hasAttribute('data-dtc-product-form')) return;
    const params = new URLSearchParams(window.location.search);
    const format = params.get('ahanda_form');
    if (format === 'ground' || format === 'bean') {
      const options = Array.from(form.querySelectorAll('[data-form-option-input], [data-form-choice]'));
      const choice = options.find(input => /öğüt|ground/i.test(input.value) === (format === 'ground'));
      if (choice) {
        choice.checked = true;
        const picker = form.querySelector('[data-form-picker]');
        if (picker) picker.value = choice.value;
      }
    }
    syncGrindState(form);
    const method = form.querySelector('[data-grind-method]');
    const requested = params.get('ahanda_method');
    if (method && !method.disabled && requested && !method.dataset.fixedGrindMethod) {
      if (Array.from(method.options || []).some(option => option.value === requested)) method.value = requested;
    }
  }

  function bindProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      restoreProductSelection(form);
      updateVariant(form);
      form.querySelectorAll('[data-option-position]').forEach((input) => {
        input.addEventListener('change', () => updateVariant(form));
      });
      const picker = form.querySelector('[data-form-picker]');
      if (picker) picker.addEventListener('change', () => { syncGrindState(form); syncDtcPurchase(form); });
      form.querySelectorAll('[data-form-choice]').forEach((input) => {
        input.addEventListener('change', () => {
          if (picker) picker.value = input.value;
          syncGrindState(form);
          syncDtcPurchase(form);
        });
      });
    });
  }

  function bindProductGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!main || !thumbs.length) return;
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const image = thumb.dataset.galleryImage;
          if (!image) return;
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = image;
          main.alt = thumb.dataset.galleryAlt || main.alt;
          thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
        });
      });
    });
  }

  function bindGuideFilters() {
    document.querySelectorAll('[data-guide-filters]').forEach((filters) => {
      const cards = Array.from(document.querySelectorAll('[data-guide-card]'));
      filters.addEventListener('click', (event) => {
        const button = event.target.closest('[data-guide-filter]');
        if (!button) return;
        const filter = button.dataset.guideFilter;
        filters.querySelectorAll('[data-guide-filter]').forEach((item) => item.classList.toggle('is-active', item === button));
        cards.forEach((card) => { card.hidden = filter !== 'all' && card.dataset.guideCard !== filter; });
        publishEvent('guide_filter', { filter });
      });
    });
  }

  function bindGuidePreview() {
    const panels = document.querySelector('[data-guide-preview-panels]');
    const landing = document.querySelector('[data-guide-landing-shell]');
    if (!panels || !landing) return;
    const query = new URLSearchParams(window.location.search).get('guide') || window.location.hash.replace('#guide=', '');
    if (!query || !panels.querySelector(`[data-guide-preview="${CSS.escape(query)}"]`)) return;
    landing.hidden = true;
    panels.hidden = false;
    panels.querySelectorAll('[data-guide-preview]').forEach((panel) => { panel.hidden = panel.dataset.guidePreview !== query; });
  }

  function bindFaqSearch() {
    document.querySelectorAll('[data-faq-page]').forEach((page) => {
      const input = page.querySelector('[data-faq-search]');
      const status = page.querySelector('[data-faq-search-status]');
      const empty = page.querySelector('[data-faq-empty]');
      const items = Array.from(page.querySelectorAll('[data-faq-item]'));
      const categories = Array.from(page.querySelectorAll('[data-faq-category]'));
      const navLinks = Array.from(page.querySelectorAll('[data-faq-nav-link]'));
      if (!input) return;

      function filterFaq() {
        const query = input.value.trim().toLocaleLowerCase('tr-TR');
        let matches = 0;
        items.forEach((item) => {
          const searchableText = item.textContent.toLocaleLowerCase('tr-TR');
          const isMatch = !query || searchableText.includes(query);
          item.hidden = !isMatch;
          if (isMatch) matches += 1;
        });
        categories.forEach((category) => {
          const hasMatch = Boolean(category.querySelector('[data-faq-item]:not([hidden])'));
          category.hidden = Boolean(query && !hasMatch);
          const link = navLinks.find((item) => item.dataset.faqNavLink === category.dataset.faqCategory);
          if (link) link.hidden = category.hidden;
        });
        if (status) {
          status.hidden = !query;
          status.textContent = query ? `${matches} soru bulundu.` : '';
        }
        if (empty) empty.hidden = !query || matches > 0;
      }

      input.addEventListener('input', filterFaq);
    });
  }

  function bindOrderTracking() {
    document.querySelectorAll('[data-order-tracking]').forEach((page) => {
      const form = page.querySelector('[data-order-tracking-form]');
      const result = page.querySelector('[data-order-tracking-result]');
      const error = page.querySelector('[data-order-tracking-error]');
      const orderOutput = page.querySelector('[data-order-tracking-order]');
      const supportLink = page.querySelector('[data-order-tracking-support-link]');
      const reset = page.querySelector('[data-order-tracking-reset]');
      if (!form || !result) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const emailInput = form.querySelector('[name="email"]');
        const orderInput = form.querySelector('[name="order_number"]');
        const email = emailInput?.value.trim() || '';
        const orderNumber = orderInput?.value.trim() || '';
        if (!email || !emailInput.checkValidity() || !orderNumber) {
          if (error) error.hidden = false;
          if (!email || !emailInput.checkValidity()) emailInput?.focus();
          else orderInput?.focus();
          return;
        }
        if (error) error.hidden = true;
        if (orderOutput) orderOutput.textContent = orderNumber;
        if (supportLink) {
          const subject = encodeURIComponent(`Sipariş takibi ${orderNumber}`);
          const body = encodeURIComponent(`Sipariş numarası: ${orderNumber}\nSipariş e-postası: ${email}`);
          supportLink.href = `mailto:${page.dataset.supportEmail || ''}?subject=${subject}&body=${body}`;
        }
        form.hidden = true;
        result.hidden = false;
        result.focus();
      });

      reset?.addEventListener('click', () => {
        form.reset();
        form.hidden = false;
        result.hidden = true;
        if (error) error.hidden = true;
        form.querySelector('[name="email"]')?.focus();
      });
    });
  }

  function bindTrackingWhatsApp() {
    document.querySelectorAll('[data-tracking-whatsapp]').forEach((banner) => {
      const input = banner.querySelector('[data-tracking-whatsapp-order]');
      const link = banner.querySelector('[data-tracking-whatsapp-link]');
      const hint = banner.querySelector('[data-tracking-whatsapp-hint]');
      const number = banner.dataset.whatsappNumber || '';
      if (!input || !link || !number) return;

      const updateLink = () => {
        const rawOrderNumber = input.value.trim().replace(/^#+/, '');
        if (!rawOrderNumber) {
          link.href = `https://wa.me/${number}`;
          return false;
        }
        const message = `Merhaba, #${rawOrderNumber} No'lu siparişim hakkında bilgi almak istiyorum.`;
        link.href = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
        return true;
      };

      input.addEventListener('input', () => {
        updateLink();
        if (hint) hint.hidden = true;
      });

      link.addEventListener('click', (event) => {
        if (!updateLink()) {
          event.preventDefault();
          if (hint) hint.hidden = false;
          input.focus();
        }
      });
    });
  }

  function bindCustomerAuth() {
    document.querySelectorAll('[data-customer-auth]').forEach((page) => {
      const recover = page.querySelector('[data-auth-recover]');
      const loginForm = page.querySelector('#CustomerLoginForm');
      const loginHeading = page.querySelector('[data-auth-login-heading]')?.closest('.customer-auth__heading');
      const loginDescription = page.querySelector('[data-auth-login-description]');
      const divider = page.querySelector('.customer-auth__divider');
      const trust = page.querySelector('.customer-auth__trust');
      const switchLink = page.querySelector('.customer-auth__switch');
      if (!recover || !loginForm) return;

      function showRecover(shouldUpdateHash) {
        recover.hidden = false;
        loginForm.hidden = true;
        if (loginHeading) loginHeading.hidden = true;
        if (divider) divider.hidden = true;
        if (trust) trust.hidden = true;
        if (switchLink) switchLink.hidden = true;
        if (shouldUpdateHash) history.replaceState(null, '', '#recover');
        recover.querySelector('input')?.focus();
      }

      function showLogin(shouldUpdateHash) {
        recover.hidden = true;
        loginForm.hidden = false;
        if (loginHeading) loginHeading.hidden = false;
        if (divider) divider.hidden = false;
        if (trust) trust.hidden = false;
        if (switchLink) switchLink.hidden = false;
        if (shouldUpdateHash) history.replaceState(null, '', window.location.pathname);
        loginForm.querySelector('input')?.focus();
      }

      page.addEventListener('click', (event) => {
        if (event.target.closest('[data-auth-recover-trigger]')) { event.preventDefault(); showRecover(true); }
        if (event.target.closest('[data-auth-login-trigger]')) { event.preventDefault(); showLogin(true); }
      });
      if (window.location.hash === '#recover') showRecover(false);
      else if (loginDescription) loginDescription.hidden = false;
    });
  }

  function bindCoffeeCalculator() {
    document.querySelectorAll('[data-coffee-calculator]').forEach((calculator) => {
      const cupsInput = calculator.querySelector('[data-coffee-cups]');
      const cupsOutput = calculator.querySelector('[data-coffee-cups-output]');
      const monthlyOutput = calculator.querySelector('[data-coffee-monthly]');
      const weeklyOutput = calculator.querySelector('[data-coffee-weekly]');
      const dailyOutput = calculator.querySelector('[data-coffee-daily]');
      if (!cupsInput || !cupsOutput || !monthlyOutput || !weeklyOutput || !dailyOutput) return;

      const formatKg = (grams) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(grams / 1000)} kg`;
      const formatGrams = (grams) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(grams)} g`;
      const update = () => {
        const cups = Math.max(1, Number(cupsInput.value || 1));
        const dailyGrams = cups * 18;
        const weeklyGrams = dailyGrams * 7;
        const monthlyGrams = dailyGrams * 30;
        cupsOutput.textContent = `${cups} fincan`;
        cupsInput.setAttribute('aria-valuetext', `${cups} fincan`);
        dailyOutput.textContent = formatGrams(dailyGrams);
        weeklyOutput.textContent = formatKg(weeklyGrams);
        monthlyOutput.textContent = formatKg(monthlyGrams);
      };

      cupsInput.addEventListener('input', update);
      update();
    });
  }

  async function addProduct(form) {
    if (form.dataset.submitting === 'true') return;
    updateVariant(form);
    if (form.dataset.variantAvailable === 'false') {
      showFormError(form, window.theme?.language === 'en' ? 'Select an available option.' : 'Stokta olan bir seçenek seçmelisin.');
      return;
    }
    const grind = syncGrindState(form);
    const formData = new FormData(form);
    if (grind.isGround && !formData.get('properties[Demleme yöntemi]')) {
      showFormError(form, window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'Select a brewing method for ground coffee.' : 'Öğütülmüş kahve için demleme yöntemini seçmelisin.');
      const method = form.querySelector('[data-grind-method]');
      if (method) method.focus();
      return;
    }
    const submit = form.querySelector('[data-add-to-cart]');
    const submitLabel = submit?.querySelector('[data-add-to-cart-label-text]');
    const originalText = submit ? (submitLabel ? submitLabel.textContent : submit.textContent) : '';
    const crossSellCard = form.closest('[data-cross-sell-card]');
    if (submit) {
      submit.disabled = true;
      const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
      if (submitLabel) submitLabel.textContent = isEnglish ? 'Adding…' : 'Ekleniyor…';
      else submit.textContent = isEnglish ? 'Adding…' : 'Ekleniyor…';
    }
    form.dataset.submitting = 'true';
    syncDtcPurchase(form);
    try {
      const response = await fetch(routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) throw new Error('Product could not be added');
      const added = await response.json();
      const quantity = Number(formData.get('quantity') || 1);
      publishEvent('add_to_cart', {
        variantId: String(added.variant_id || added.id), quantity,
        item_list_name: form.dataset.itemListName || undefined,
        item_id: String(added.product_id), item_name: added.product_title,
        ecommerce: {
          currency: window.theme?.currency || 'TRY', value: added.price / 100 * quantity,
          items: [{ item_id: String(added.product_id), item_name: added.product_title,
            item_variant: added.variant_title, variant_id: String(added.variant_id || added.id),
            price: added.price / 100, quantity }]
        }
      });
      await refreshDrawer(true);
    } catch (error) {
      showFormError(form, window.theme?.language === 'en' || window.location.pathname.startsWith('/en/') ? 'We could not add this product to the cart. Please try another option.' : 'Bu ürünü şu anda sepete ekleyemiyoruz. Lütfen başka bir seçenek dene.');
    } finally {
      form.dataset.submitting = 'false';
      if (submit) {
        submit.disabled = form.dataset.variantAvailable === 'false';
        const isEnglish = window.theme?.language === 'en' || window.location.pathname.startsWith('/en/');
        if (submitLabel) submitLabel.textContent = originalText || submit.dataset.addToCartLabel || (isEnglish ? 'Add to cart' : 'Sepete ekle');
        else submit.textContent = originalText || (isEnglish ? 'Add to cart' : 'Sepete ekle');
      }
      updateVariant(form);
      syncDtcPurchase(form);
    }
  }

  document.addEventListener('click', (event) => {
    const crossSellLink = event.target.closest('[data-cross-sell-link]');
    if (crossSellLink) {
      publishEvent('select_item', {
        list_name: crossSellLink.dataset.listName || 'cross_sell_pdp',
        item_id: crossSellLink.dataset.productId || undefined,
        item_name: crossSellLink.dataset.productTitle || undefined
      });
    }
    const funnelLink = event.target.closest('[data-funnel-link]');
    if (funnelLink) publishEvent('funnel_click', { name: funnelLink.dataset.funnelLink, label: funnelLink.dataset.funnelLabel || '', href: funnelLink.href || '' });
    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger) { event.preventDefault(); openDrawer(); return; }
    if (event.target.closest('[data-cart-close]') || event.target.closest('[data-cart-overlay]')) { closeDrawer(); return; }
    const searchTrigger = event.target.closest('[data-search-trigger]');
    if (searchTrigger) { event.preventDefault(); openSearch(); return; }
    if (event.target.closest('[data-search-close]') || event.target.closest('[data-search-overlay]')) { closeSearch(); return; }
    if (event.target.closest('[data-filter-trigger]')) { openFilters(); return; }
    if (event.target.closest('[data-filter-close]') || event.target.closest('[data-filter-overlay]')) { closeFilters(); return; }
    const mobileTrigger = event.target.closest('[data-mobile-menu-trigger]');
    if (mobileTrigger) {
      const menu = document.querySelector('[data-mobile-menu]');
      if (!menu) return;
      const isOpen = !menu.hidden;
      menu.hidden = isOpen;
      mobileTrigger.setAttribute('aria-expanded', String(!isOpen));
      return;
    }
    const productQuantity = event.target.closest('[data-product-quantity]');
    if (productQuantity) {
      const form = productQuantity.closest('[data-product-form]');
      const input = form && form.querySelector('[data-product-quantity-input]');
      const hidden = form && form.querySelector('[data-quantity-value]');
      if (input) {
        const current = Number(input.value || 1);
        const next = productQuantity.dataset.productQuantity === 'increase' ? current + 1 : current - 1;
        input.value = Math.max(1, next);
        if (hidden) hidden.value = input.value;
      }
      return;
    }
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) {
      const line = Number(quantity.dataset.line);
      const current = Number(quantity.dataset.current || 1);
      const next = quantity.dataset.direction === 'increase' ? current + 1 : current - 1;
      const cartPage = quantity.closest('[data-cart-page]');
      if (cartPage) changeCartPageLine(line, Math.max(0, next)).catch(() => {});
      else changeLine(line, Math.max(0, next)).catch(() => {});
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      const cartPage = remove.closest('[data-cart-page]');
      if (cartPage) changeCartPageLine(Number(remove.dataset.line), 0).catch(() => {});
      else changeLine(Number(remove.dataset.line), 0).catch(() => {});
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawer();
      closeSearch();
      closeFilters();
      document.querySelectorAll('[data-mega-menu][open]').forEach((menu) => { menu.open = false; });
      document.querySelectorAll('[data-language-switcher].is-open').forEach((switcher) => {
        switcher.classList.remove('is-open');
        const trigger = switcher.querySelector('[data-language-switcher-trigger]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-predictive-input]')) schedulePredictiveSearch(event.target);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addProduct(form);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-grind-method]')) syncDtcPurchase(event.target.closest('[data-product-form]'));
    if (event.target.matches('[data-collection-sort]')) {
      publishEvent('collection_sort', { value: event.target.value });
      return;
    }
    if (!event.target.matches('[data-product-quantity-input]')) return;
    const form = event.target.closest('[data-product-form]');
    const hidden = form && form.querySelector('[data-quantity-value]');
    const value = Math.max(1, Number(event.target.value || 1));
    event.target.value = value;
    if (hidden) hidden.value = value;
  });

  bindProductForms();
  bindProductGalleries();
  bindMegaMenus();
  bindLanguageSwitcher();
  bindGuideFilters();
  bindGuidePreview();
  bindFaqSearch();
  bindOrderTracking();
  bindTrackingWhatsApp();
  bindCustomerAuth();
  bindCoffeeCalculator();
  window.ahandaInitQuiz?.(publishEvent);
  bindLeadAttribution();
  bindDtcAnalytics();
})();
